import type { FC } from "react"
import React from "react"
import type { GetServerSideProps } from "next"
import { Stack } from "@chakra-ui/react"
import invariant from "tiny-invariant"
import isEqual from "lodash.isequal"

import MainLayout from "ui/layouts/main/MainLayout"
import MainLayoutHead from "ui/layouts/main/MainLayoutHead"
import MainLayoutContainer from "ui/layouts/main/MainLayoutContainer"
import MainLayoutFooter from "ui/layouts/main/MainLayoutFooter"
import ErrorBoundary from "common/components/ErrorBoundary"
import type { DescriptionException } from "proposal/types/exception"
import {
  fetchDescriptionExceptionsByGovernorId,
  getProposalTitle,
} from "proposal/helpers/description"
import type {
  Chain,
  ChainId,
  Governor,
  Organization,
  Proposal,
} from "query/graphql"
import {
  ProposalStatus,
  VotesSortBy,
  useProposalHeaderQuery,
  useProposalDetailsQuery,
} from "query/graphql"
import GovernanceProvider from "governance/context"
import { getGovernanceIds } from "governance/helpers/governance"
import {
  getOrganizationSocialPreviewImage,
  isMultiChainTokenIds,
} from "organization/helpers/organization"
import ProposalHeader from "proposal/components/ProposalHeader"
import ProposalCharts from "proposal/components/ProposalCharts"
import ProposalDetails from "proposal/components/ProposalDetails"
import ProposalStatusHistory from "proposal/components/ProposalStatusHistory"
import ProposalVotesCast from "proposal/components/ProposalVotesCast"
import ProposalMobileButtons from "proposal/components/ProposalMobileButtons"
import { DescriptionExceptionsProvider } from "proposal/providers/DescriptionExceptionsProvider"
import { BREADCRUMB } from "common/constants/breadcrumb"
import { ROUTES } from "common/constants/routes"
import { isValidAccountId } from "web3/helpers/accountId"
import { fetchGovernanceById } from "governance/helpers/fetch"
import {
  fetchProposalById,
  fetchProposalByOnchainId,
} from "proposal/helpers/fetch"
import {
  fetchGovernorsByOrganization,
  fetchOrganizationBySlug,
} from "organization/helpers/fetcher"
import {
  getWhitelabelBreadcrumb,
  getWhiteLabelRoute,
} from "whitelabel/utils/breadcrumb"
import { isCorrectSlug, isWhitelabel } from "whitelabel/hooks/useIsWhiteLabel"
import TallyProposalImpactOverviewSummary from "tallyProposal/components/TallyProposalImpactOverviewSummary"
import { OrganizationProvider } from "organization/providers/OrganizationProvider"
import MainLayoutHeader from "ui/layouts/main/MainLayoutHeader"
import { OrganizationMenu } from "ui/layouts/main/header/HeaderDAO"
import DaoLayoutBreadcrumb from "ui/layouts/dao/DaoLayoutBreadcrumb"
import ProposalPendingVotes from "proposal/components/ProposalPendingVotes"
import { ProposalProvider } from "proposal/providers/ProposalProvider"
import ProposalMirrored from "proposal/components/ProposalMirrored"
import { spacing } from "theme/foundations/spacing"
import { WORMHOLE_SLUGS } from "governance/components/wormhole/constants"

type Props = {
  chainId: ChainId
  proposal: Proposal
  governors: Governor[]
  organization: Organization
  descriptionExceptions: DescriptionException[]
  chains: Chain[]
  isWhiteLabel: boolean
}

const ProposalPage: FC<Props> = ({
  chainId,
  proposal,
  governors,
  organization,
  descriptionExceptions,
  isWhiteLabel,
}) => {
  const { name, slug } = organization

  const governanceIds = getGovernanceIds(governors)
  const isMultiGov = governanceIds.length > 1

  const isMultiChain = isMultiChainTokenIds(organization.tokenIds)

  // custom logic for ENS
  const isReverseOrder = slug === "ens"

  invariant(proposal.governor, "proposal.governor is required")

  const {
    governor: { id: governorId },
  } = proposal

  const proposalTitle = getProposalTitle(proposal.metadata.description)

  const title = isWhiteLabel
    ? `${name} | ${proposalTitle}`
    : `Tally | ${name} | ${proposalTitle}`

  const { data: proposalDetails, isLoading: isLoadingDetails } =
    useProposalDetailsQuery({
      input: {
        id: proposal.id,
      },
      votesInput: {
        filters: {
          proposalId: proposal.id,
        },
        sort: {
          sortBy: VotesSortBy.Amount,
          isDescending: true,
        },
        page: {
          limit: 500, // NOTE: this is not an accident.
        },
      },
    })

  const { data: proposalHeaderData, isLoading: isLoadingHeaderData } =
    useProposalHeaderQuery({
      input: {
        id: proposal.id,
      },
    })

  const preview = {
    title,
    image: getOrganizationSocialPreviewImage(organization),
    description: proposalTitle,
  }

  const breadcrumb = isWhiteLabel
    ? getWhitelabelBreadcrumb(
        BREADCRUMB.governance.proposal.id(
          slug,
          name,
          proposal.id,
          isWhiteLabel,
        ),
      )
    : BREADCRUMB.governance.proposal.id(slug, name, proposal.id, isWhiteLabel)

  const customHead = getCustomHead({
    proposal,
    slug,
    govIdQueryParam:
      organization.governorIds.length > 1 && proposal.governor
        ? proposal.governor.id
        : "",
  })

  return (
    <ErrorBoundary section="Proposal">
      <DescriptionExceptionsProvider
        descriptionExceptions={descriptionExceptions}
      >
        <OrganizationProvider governors={governors} organization={organization}>
          <GovernanceProvider governor={proposal.governor}>
            <ProposalProvider proposal={proposal}>
              <MainLayout>
                <MainLayoutHead
                  customHead={customHead}
                  preview={preview}
                  title={title}
                />
                <MainLayoutHeader
                  activeOrganizationMenu={OrganizationMenu.Proposals}
                  isWhiteLabel={isWhiteLabel}
                  organization={organization}
                />
                <DaoLayoutBreadcrumb breadcrumb={breadcrumb} />
                <MainLayoutContainer>
                  <Stack spacing={spacing.containerGap}>
                    <ErrorBoundary section="ProposalHeader">
                      <ProposalHeader
                        data={proposalHeaderData}
                        governorId={governorId}
                        governorName={name}
                        isActive={proposal.status === ProposalStatus.Active}
                        isLoading={isLoadingHeaderData}
                        isMultiChain={isMultiChain}
                        isMultiGov={isMultiGov}
                        proposalId={proposal.id}
                        slug={slug}
                      />
                    </ErrorBoundary>
                    <Stack
                      direction={{ base: "column", lg: "row" }}
                      mb={{ base: 6, lg: 0 }}
                      spacing={spacing.containerGap}
                    >
                      <Stack
                        flex={1}
                        flexDirection={
                          isReverseOrder ? "column-reverse" : "column"
                        }
                        maxWidth={{ base: "100%", lg: "70%" }}
                        spacing={spacing.containerGap}
                      >
                        <ErrorBoundary section="ProposalCharts">
                          <ProposalCharts
                            onchainId={proposal.onchainId}
                            proposalId={proposal.id}
                            slug={slug}
                          />
                        </ErrorBoundary>
                        <ErrorBoundary section="ProposalDetails">
                          <ProposalDetails
                            chainId={chainId}
                            data={proposalDetails}
                            governorId={governorId}
                            isLoading={isLoadingDetails}
                            proposalId={proposal.onchainId ?? proposal.id}
                            slug={slug}
                            snapshotURL={proposal.metadata.snapshotURL}
                          />
                        </ErrorBoundary>
                      </Stack>
                      <Stack
                        spacing={spacing.containerGap}
                        w={{ base: "100%", lg: "30%" }}
                      >
                        <ErrorBoundary section="ProposalMirrored">
                          <ProposalMirrored
                            organization={organization}
                            proposalId={proposal.id}
                          />
                        </ErrorBoundary>

                        {proposal.status === ProposalStatus.Active &&
                        WORMHOLE_SLUGS.includes(organization.slug) ? (
                          <ErrorBoundary section="ProposalPendingVotes">
                            <ProposalPendingVotes proposalId={proposal.id} />
                          </ErrorBoundary>
                        ) : null}

                        <ErrorBoundary section="ProposalVotesCast">
                          <ProposalVotesCast
                            isWhiteLabel={isWhiteLabel}
                            proposalId={proposal.id}
                            slug={slug}
                          />
                        </ErrorBoundary>

                        {proposalDetails?.proposal ? (
                          <ErrorBoundary section="TallyProposalImpactOverviewSummary">
                            <TallyProposalImpactOverviewSummary
                              chainId={proposal.governor.chainId}
                              executablesLength={
                                proposalDetails.proposal?.executableCalls?.map(
                                  ({ target }) => target,
                                )?.length ?? 0
                              }
                              proposalId={proposalDetails.proposal.id}
                            />
                          </ErrorBoundary>
                        ) : null}
                        <ErrorBoundary section="ProposalStatusHistory">
                          <ProposalStatusHistory
                            governorId={governorId}
                            proposalId={proposal.id}
                            slug={slug}
                          />
                        </ErrorBoundary>
                      </Stack>
                    </Stack>
                    <ErrorBoundary section="ProposalMobileButtons">
                      <ProposalMobileButtons
                        proposalId={proposal.id}
                        slug={slug}
                      />
                    </ErrorBoundary>
                  </Stack>
                </MainLayoutContainer>
                <MainLayoutFooter isWhiteLabel={isWhiteLabel} />
              </MainLayout>
            </ProposalProvider>
          </GovernanceProvider>
        </OrganizationProvider>
      </DescriptionExceptionsProvider>
    </ErrorBoundary>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context
  const { governanceId: governanceIdParam, proposalId, govId } = query
  const slug = governanceIdParam as string // `slug` can mean either id or slug
  const govIdQueryParam = govId as string

  // Find governance by id and redirect to /gov/:slug
  if (isValidAccountId(slug)) {
    const governance = await fetchGovernanceById(slug)

    if (governance && governance.organization) {
      return {
        redirect: {
          permanent: false,
          destination: ROUTES.governance.proposal.id(
            governance.organization.slug,
            proposalId as string,
            govIdQueryParam ?? "",
          ),
        },
      }
    }
  }

  if (isWhitelabel(context)) {
    if (!isCorrectSlug(context, slug)) {
      return {
        redirect: {
          permanent: false,
          destination: "/404",
        },
      }
    }
  }

  const organization = await fetchOrganizationBySlug(slug, context)

  if (!organization) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const governors = await fetchGovernorsByOrganization(organization.id, context)

  if (!governors.length) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const [governor] = governors

  invariant(
    typeof proposalId === "string",
    '"proposalId" should be defined on URL',
  )

  const findProposal = async () => {
    let proposalByOnchainId = null

    const proposalById = await fetchProposalById(proposalId)

    if (proposalById) {
      return proposalById
    }

    // we don't know which governor id to use ahead of time when using onchainId
    // for proposal query; so try all of them until it works
    for (const gov of governors) {
      proposalByOnchainId = await fetchProposalByOnchainId(
        proposalId,
        govIdQueryParam ?? (gov.id as string),
      )

      if (proposalByOnchainId) {
        return proposalByOnchainId
      }
    }

    return null
  }

  const proposal = await findProposal()

  if (!proposal) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const { descriptionExceptions } =
    await fetchDescriptionExceptionsByGovernorId(organization.governorIds)

  if (proposal.onchainId && proposal.onchainId !== proposalId) {
    return {
      redirect: {
        permanent: true,
        destination: isWhitelabel(context)
          ? getWhiteLabelRoute(
              ROUTES.governance.proposal.id(
                organization.slug,
                proposal.onchainId,
                govIdQueryParam ?? "",
              ),
            )
          : ROUTES.governance.proposal.id(
              organization.slug,
              proposal.onchainId,
              govIdQueryParam ?? "",
            ),
      },
    }
  }

  const descriptionExceptionFound = descriptionExceptions.find((exception) => {
    return (
      isEqual(exception.proposalId, proposal.id) ||
      isEqual(exception.proposalId, proposal.onchainId)
    )
  })

  return {
    props: {
      chainId: governor.chainId,
      proposal,
      organization,
      governors,
      descriptionExceptions: descriptionExceptionFound
        ? [descriptionExceptionFound]
        : [],
      isWhiteLabel: isWhitelabel(context),
    },
  }
}

type CustomHeadProps = {
  slug: string
  proposal: Proposal
  govIdQueryParam: string
}
const getCustomHead: FC<CustomHeadProps> = ({
  slug,
  proposal,
  govIdQueryParam,
}) => {
  const proposalRoute = ROUTES.governance.proposal.id(
    slug,
    proposal.onchainId ?? proposal.id,
    govIdQueryParam ?? "",
  )
  const proposalUrl = `${process.env.NEXT_PUBLIC_TALLY_SIWE_URI}${proposalRoute}`

  const imgSrc = `${
    process.env.NEXT_FARCARTER_FRAMES_IMAGE_URL
  }/api/proposal/image/frame-1?proposalId=${
    proposal.onchainId ?? proposal.id
  }&governorSlug=${slug}`

  const actionText =
    proposal.status === ProposalStatus.Active ||
    proposal.status === ProposalStatus.Extended
      ? "Vote"
      : "View Proposal"

  return (
    <>
      <meta content="vNext" name="fc:frame" />

      <meta content={imgSrc} name="fc:frame:image" />
      <meta content="1.91:1" name="fc:frame:image:aspect_ratio" />

      <meta content={actionText} name="fc:frame:button:1" />
      <meta content="link" name="fc:frame:button:1:action" />
      <meta content={proposalUrl} name="fc:frame:button:1:target" />
    </>
  )
}

export default ProposalPage
